import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  const sign = n < 0 ? '-$' : '$';
  return `${sign}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Phase 25.3 — Statement Generation confirmation dialog.
 *
 * Shows the pre-flight summary (Carrier / Period / Recipients / Totals) so the
 * user must explicitly confirm. Duplicate generation is blocked here + at the API.
 */
export default function GenerateStatementsDialog({ open, onClose, importId, onGenerated }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !importId) return;
    setLoading(true);
    accountingAPI.statementPreview(importId)
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load preview'))
      .finally(() => setLoading(false));
  }, [open, importId]);

  const confirm = async () => {
    setGenerating(true);
    try {
      const { data } = await accountingAPI.generateStatements(importId);
      toast.success(`Generated ${data.statements.length} statement(s)`);
      onGenerated?.(data.statements);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !generating) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="generate-statements-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={16} /> Generate Statements
          </DialogTitle>
          <DialogDescription>
            One immutable Statement will be generated per confirmed recipient. Once generated, Statements can no longer be edited — corrections use future Manual Adjustments.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
        ) : !preview ? (
          <div className="py-10 text-center text-slate-400">No preview data</div>
        ) : (
          <div className="space-y-3">
            <SummaryRow label="Carrier"                  value={preview.carrier_name || '—'} />
            <SummaryRow label="Statement Period"         value={`${preview.period_label} (day ${preview.period_year}-${String(preview.period_month).padStart(2,'0')}-${String(preview.period_day).padStart(2,'0')})`} />
            <SummaryRow label="Reconciled Lines"         value={preview.reconciled_lines} />
            <SummaryRow label="Recipients"               value={preview.recipients} />
            <SummaryRow label="Estimated Statements"     value={preview.estimated_statements} bold />
            <div className="border-t border-slate-200 my-2" />
            <SummaryRow label="Gross Positive Amount"    value={money(preview.positive_amount)} tone="emerald" />
            <SummaryRow label="Total Chargebacks"        value={money(preview.negative_amount)} tone={preview.negative_amount < 0 ? 'red' : 'slate'} />
            <SummaryRow label="Net Total Amount To Pay"  value={money(preview.net_total)} bold big />

            {!preview.generation_allowed && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2 mt-2"
                   data-testid="preview-blocked">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Cannot generate</div>
                  <div>{preview.blocked_reason}</div>
                </div>
              </div>
            )}

            {preview.generation_allowed && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 flex items-start gap-2 mt-2"
                   data-testid="preview-ok">
                <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
                <div>Ready to generate. Statements are immutable once created.</div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={generating}>Cancel</Button>
          <Button
            onClick={confirm}
            disabled={generating || loading || !preview?.generation_allowed}
            data-testid="confirm-generate-btn"
          >
            {generating
              ? (<><Loader2 size={13} className="mr-1 animate-spin" /> Generating…</>)
              : 'Generate Statements'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value, bold, big, tone }) {
  const toneCls = tone === 'emerald' ? 'text-emerald-700'
                : tone === 'red'     ? 'text-red-700'
                : 'text-slate-800';
  return (
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={`${bold ? 'font-semibold' : ''} ${big ? 'text-lg' : 'text-sm'} ${toneCls} font-mono`}>{value}</div>
    </div>
  );
}
